import React from 'react';

import { Box, Stack, Text, xcss } from '@atlaskit/primitives';

import TableTree, { Cell, Header, Headers, Row, Rows } from '@atlaskit/table-tree';

import StatusSuccessIcon from '@atlaskit/icon/core/status-success';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import TaskToDoIcon from '@atlaskit/icon/core/task-to-do';

import { token } from '@atlaskit/tokens';
import Heading from '@atlaskit/heading';
import type { HealthcheckEntry, HealthcheckStatus } from '../../common/types.tsx';
import HealthcheckSuggestedAction from '../healthcheck-suggested-action/index.tsx';

const HealthcheckIcon = (props: HealthcheckEntry) => {
	return (
		<>
			{props.status.statusType === 'SUCCESS' && (
				<StatusSuccessIcon color={token('color.icon.success')} label="Success" />
			)}
			{props.status.statusType === 'FAILURE' && (
				<CrossCircleIcon color={token('color.icon.danger')} label="Unsuccessful" />
			)}
			{props.status.statusType === 'NOT_TRIED' && (
				<TaskToDoIcon color={token('color.icon')} label="Not Tried" />
			)}
		</>
	);
};

const getColorFromStatus = (status: HealthcheckStatus) => {
	if (status.statusType === 'NOT_TRIED') {
		return 'color.text.disabled';
	}
	return 'color.text';
};

const HealthcheckTitle = (props: HealthcheckEntry) => {
	return <Text color={getColorFromStatus(props.status)}>{props.name}</Text>;
};

const HealthcheckComment = (props: HealthcheckEntry) => {
	const message = 'message' in props.status ? props.status.message : '';
	return <Text color={getColorFromStatus(props.status)}>{message}</Text>;
};

type Item = {
	id: string;
	content: HealthcheckEntry;
	hasChildren: boolean;
	children?: Item[];
};

const convertToArray = (healthcheckEntries: HealthcheckEntry[]): Item[] => {
	const wrapWithItem = (
		id: string,
		content: HealthcheckEntry,
		hasChildren: boolean,
		children?: Item[],
	) => {
		return { id, content, hasChildren, children };
	};

	return healthcheckEntries.map((entry, index) => {
		return wrapWithItem(`entry-${index}`, entry, false);
	});
};

const HealthcheckTable = (props: {
	title: string;
	description: string;
	healthcheckEntries: HealthcheckEntry[] | null;
}): React.JSX.Element => {
	const healthcheckItems = props.healthcheckEntries
		? convertToArray(props.healthcheckEntries)
		: undefined; // Loading state

	return (
		<Stack space="space.100">
			<Heading size="medium">{props.title}</Heading>
			<Text>{props.description}</Text>
			<TableTree label="Healthcheck Table">
				<Headers>
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					<Header width="100px">Status</Header>
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					<Header width="50%">Item</Header>
					{/* eslint-disable-next-line @atlassian/i18n/no-literal-string-in-jsx */}
					<Header width="50%">Details</Header>
				</Headers>
				<Rows
					items={healthcheckItems}
					render={(item: Item) => {
						const { content } = item;

						const row = (
							<Row key={item.id} itemId={item.id} hasChildren={item.hasChildren}>
								<Cell>
									<HealthcheckIcon {...content} />
								</Cell>
								<Cell>
									<HealthcheckTitle {...content} />
								</Cell>
								<Cell>
									<HealthcheckComment {...content} />
								</Cell>
							</Row>
						);

						if (content.status.statusType === 'FAILURE') {
							return (
								<>
									{row}
									<Row
										key={`${item.id}-suggested-action`}
										itemId={`${item.id}-suggested-action`}
										hasChildren={false}
									>
										<Box xcss={suggestedActionCellStyles} role="cell">
											<HealthcheckSuggestedAction
												error={`Step "${content.name}" failed due to reason: "${
													content.status.message
												}".`}
												action={content.suggestedAction ?? null}
												link={content.suggestedActionLink ?? null}
											/>
										</Box>
									</Row>
								</>
							);
						}

						return row;
					}}
				/>
			</TableTree>
		</Stack>
	);
};

export default HealthcheckTable;
const suggestedActionCellStyles = xcss({ width: '100%', padding: 'space.200' });
